import {useCallback, useMemo} from "react";
import {IvrProfileDataSourceQuery, useIvrProfileDataSourceQuery} from "@/components/ivr/profiles/queries.generated";
import {GroupType} from "@/api";
import {createTargets, Target, TargetType} from "@/components/TargetSelector";

type Ivr = IvrProfileDataSourceQuery["currentIvr"]["items"][number];
type IvrProfileTargets = {
    extensions: IvrProfileDataSourceQuery["users"]["items"];
    faxes: IvrProfileDataSourceQuery["faxes"]["items"];
    groups: IvrProfileDataSourceQuery["groups"]["items"];
    callQueues: IvrProfileDataSourceQuery["groups"]["items"];
    ivrs: IvrProfileDataSourceQuery["ivrTargets"]["items"];
};

export type DataSource = {
    loading: boolean;
    ivr: Ivr;
    rawTargets: IvrProfileTargets;
    targets: Target[];
    activeProfile: number;

    refetch: () => Promise<Ivr>;
};

export const useDataSource = (ivrId: number, domainId: number): DataSource => {
    const query = useIvrProfileDataSourceQuery({
        variables: {
            ivrId,
            domainId,
        },
        fetchPolicy: "no-cache",
    });

    const refetch = useCallback(async () => {
        const result = await query.refetch();
        return result.data.currentIvr.items[0];
    }, [query]);

    const rawTargets = useMemo(() => {
        if (!query.data) return undefined;
        return {
            extensions: query.data?.users.items,
            faxes: query.data?.faxes.items,
            groups: query.data?.groups.items.filter((g) => g.groupType !== GroupType.CallQueue),
            callQueues: query.data?.groups.items.filter((g) => g.groupType === GroupType.CallQueue),
            ivrs: query.data?.ivrTargets.items,
        };
    }, [query.data]);

    const targets: Target[] = useMemo(() => {
        if (!rawTargets) return [];
        return [
            ...createTargets(rawTargets.extensions, TargetType.EXTENSION),
            ...createTargets(rawTargets.faxes, TargetType.FAX),
            ...createTargets(rawTargets.groups, TargetType.GROUP),
            ...createTargets(rawTargets.callQueues, TargetType.CALL_QUEUE),
            ...createTargets(rawTargets.ivrs, TargetType.IVR),
        ];
    }, [rawTargets]);

    if (query.loading)
        return {
            loading: true,
            ivr: undefined,
            rawTargets: undefined,
            targets: undefined,
            activeProfile: undefined,
            refetch: undefined,
        };

    const ivr = query.data.currentIvr.items[0];
    ivr.profiles.sort((a, b) => a.id - b.id);

    const activeProfile = ivr.profiles.find((p) => p.active).id; // one must be active

    return {
        loading: false,
        ivr,
        activeProfile,
        rawTargets,
        targets,
        refetch,
    };
};
